import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slide,
  Typography,
  Box,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled, keyframes } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from '@mui/icons-material/Visibility';
import TelegramIcon from "@mui/icons-material/Telegram";
import PropTypes from "prop-types";
import { IconGeneralfdbk } from "Icons";
import { withUserContext } from "context";

// Slide Transition from Bottom
const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Fade-in and Scale Animation
const fadeInScale = keyframes`
    from {
        opacity: 0;
        transform: scale(0.9);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "8px",
    animation: `${fadeInScale} 300ms cubic-bezier(0.4, 0, 0.2, 1)`,
    transformOrigin: "center center",
    boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
  },
}));

const AnimatedCloseButton = styled(IconButton)(({ theme }) => ({
  transition: "all 250ms ease-in-out",
  color: "#1e293b",
  maxWidth: "40px",
  maxHeight: "40px",
  "&:hover": {
    backgroundColor: "rgba(255,99,71,0.1)",
    transform: "rotate(90deg)",
    color: "#ee3349",
  },
  "&:active": {
    transform: "scale(0.9)",
  },
}));

const RevealAnimation = styled("div")(() => ({
  opacity: 0,
  transform: "translateY(20px)",
  animation: `${fadeInScale} 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
  animationDelay: "200ms",
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const GetFeedbackModal = ({ isOpen, onClose, onSubmit, ctx_user }) => {
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  const { user } = ctx_user;
  const member_id = user.id;
  const acad_year_id = user.acadYearId;
  const goi_id = user.goiId;
  const organizationId = user.organizationId;
  const MAX_CHAR_LIMIT = 500;

  const handleAttachmentPreview = () => {
    if (!attachment) return;
    setOpenPreview(true);
  };

  const handleAttachmentUpload = (event) => {
    const file = event.target.files[0];
    if (file  && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result;
            setAttachment(base64Image);
      };
      reader.readAsDataURL(file);
      setAttachmentName(file.name);
  }
  };

  const isInputValid = (value) => {
    return value.trim().length > 0;
  };

  const handleSubmit = () => {
    if (!isInputValid(description) || description.length > MAX_CHAR_LIMIT) return;

    const formData = {
      ticketId: 0,
      ticketType: "general_feedback",
      description: description.trim(),
      attachment: attachment,
      memberId: member_id,
      acadYearId: acad_year_id,
      goiId: goi_id,
      isCompleted: 0,
      isAssigned: 0,
      organizationId: organizationId,
    };
    onSubmit(formData);
    onClose();
  };

const handleDescriptionChange = (e) => {
  const value = e.target.value;
  if (value.length <= MAX_CHAR_LIMIT) {
    setDescription(value);
  }
};

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="md"
      fullWidth
      // sx={{ marginBottom: "64px" }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          color: "black",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            // gap: 1
          }}
        >
          <Box sx={{ display: "flex" }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", textAlign: "center" }}
            >
              GENERAL FEEDBACK
            </Typography>
            <Box sx={{ marginLeft: 2 }}>
              <IconGeneralfdbk />
            </Box>
          </Box>
          <Box
            sx={{
              alignItems: "center",
              textAlign: "center",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <Typography
              style={{
                textAlign: "center",
                fontSize: "15px",
                fontWeight: "100",
                opacity: "0.6",
              }}
            >
              Send us your Feedback
            </Typography>
          </Box>
        </Box>
        <AnimatedCloseButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </AnimatedCloseButton>
      </DialogTitle>

      <div style={{ padding: "0px 30px" }}>
        <Typography style={{ fontWeight: "bold" }}>
          Please provide your feedback or any Other Concerns?
        </Typography>
        <TextField
          fullWidth
          label="Max 500 Characters"
          variant="outlined"
          value={description}
          onChange={handleDescriptionChange}
          margin="normal"
          required
          multiline // Enable multiline input
          rows={4} // You can adjust this to your preferred height
          slotProps={{
            input: {
              maxLength: MAX_CHAR_LIMIT,
            },
          }}          
          error={description.length > MAX_CHAR_LIMIT}
          helperText={
            description.length >= MAX_CHAR_LIMIT
              ? "You have reached the maximum limit of 500 characters."
              : ""
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              color: "#1e293b",
            },
          }}
        />
        <RevealAnimation>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                marginTop: 2,
                border: "1px solid rgba(0,0,0,0.23)",
                borderRadius: "12px",
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Button
                variant="text"
                disabled={!attachment}
                onClick={handleAttachmentPreview}
                startIcon={<VisibilityIcon />}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  borderRight: "1px solid rgba(0,0,0,0.23)",
                  color: "#1e293b",
                }}
              >
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                />
              </Button>

              <Button
                component="label"
                variant="text"
                startIcon={<AttachFileIcon />}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  color: "#1e293b",
                  height: "44px",
                }}
              >
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleAttachmentUpload}
                />
              </Button>
            </Box>

            {attachment && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1, width: "100%" }}
              >
                Attachment: {attachmentName}
              </Typography>
            )}
          </Box>
        </RevealAnimation>

        <div></div>
      </div>

      <DialogActions>{/* Optional action buttons */}</DialogActions>
      <Button
        fullWidth
        startIcon={<TelegramIcon />}
        variant="contained"
        sx={{
          marginBottom: 2,
          marginTop: 1,
          borderRadius: "12px",
          backgroundColor: "#1e293b",
          width: "92%", // Reduce width to 50%
          alignSelf: "center", // Center the button
          "&:hover": {
            backgroundColor: "#334155",
          },
        }}
        onClick={handleSubmit}
        disabled={!isInputValid(description) || description.length > MAX_CHAR_LIMIT}
      >
        Send Your Request
      </Button>
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Attachment Preview</DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          {attachment && (
            <img
              src={attachment}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </StyledDialog>
  );
};

GetFeedbackModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // description: PropTypes.string,
  // children: PropTypes.node
};

GetFeedbackModal.defaultProps = {
  description: "",
  children: null,
};

export default withUserContext(GetFeedbackModal);
